import {
  CreateSupportResponse,
  DocumentTransaction,
  SupportTeam,
} from "@/app/services/supportService/createSupport";
import { selectedIdfromTableRowTransaction } from "@/app/store";
import { SupportCategoryEnum } from "@/app/utils/supportCategoryEnum";
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useGetAllSupports } from "@/app/hooks/useGetAllSupports";
import { useSearchParams } from "react-router-dom";
import { supportService } from "@/app/services/supportService";
import { useDebounce } from "@/app/hooks/useDebounce";
import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";
import { configService } from "@/app/services/configService";
import {
  SupportRequest,
  SupportRequestStatusEnum,
} from "@/app/entities/SupportRequest";
import { useAuth } from "@/app/hooks/useAuth";
import { useShowSupportRequests } from "@/app/hooks/useShowSupportRequests";
import { SUPPORT_REQUEST_COUNTS_QUERY_KEY } from "@/app/hooks/useSupportRequestCounts";
import { supportRequestService } from "@/app/services/supportRequestService";
import { ListMySupportRequestsResponse } from "@/app/services/supportRequestService/listAllSupport";
import { toast } from "@/view/components/ui/use-toast";
import { AxiosError } from "axios";
import {
  SUPPORT_LIST_QUERY_KEY,
  SupportListTab,
} from "./components/SupportRequestQueue/useSupportQueue";

const openInNewTab = (path: string) =>
  window.open(path, "_blank", "noopener,noreferrer");

// links abertos em nova aba: /support?transacao=<id> e /support?chamado=<número>
export const SUPPORT_TRANSACTION_PARAM = "transacao";
export const SUPPORT_REQUEST_PARAM = "chamado";

// chamados não passam pela busca do suporte: vêm da listagem de chamados
export const SUPPORT_REQUESTS_CATEGORY = "SUPPORT_REQUESTS" as const;

export type SearchCategory =
  | SupportCategoryEnum.CUSTOMER
  | SupportCategoryEnum.USER
  | SupportCategoryEnum.TRANSACTION
  | SupportCategoryEnum.TEAMS
  | typeof SUPPORT_REQUESTS_CATEGORY;

export const SEARCH_CATEGORIES: SearchCategory[] = [
  SUPPORT_REQUESTS_CATEGORY,
  SupportCategoryEnum.TRANSACTION,
  SupportCategoryEnum.USER,
  SupportCategoryEnum.CUSTOMER,
  SupportCategoryEnum.TEAMS,
];

const PEOPLE_CATEGORIES = [
  SupportCategoryEnum.CUSTOMER,
  SupportCategoryEnum.USER,
  SupportCategoryEnum.TEAMS,
] as const;

type PeopleCategory = (typeof PEOPLE_CATEGORIES)[number];

const SEARCH_PAGE_SIZE = 50;
const SUPPORT_REQUESTS_PAGE_SIZE = 20;

type SearchPage = CreateSupportResponse;

const EMPTY_TOTALS = {
  sellers: 0,
  customers: 0,
  transactions: 0,
  teams: 0,
  documentsTransactions: 0,
};

const totalsOf = (data?: { pages: SearchPage[] }) =>
  data?.pages[0]?.pagination?.totals ?? EMPTY_TOTALS;

const nextSkipOf = (
  lastPage: SearchPage,
  _pages: unknown,
  lastSkip: number,
) => {
  const nextSkip = lastSkip + SEARCH_PAGE_SIZE;
  const totals = lastPage.pagination?.totals ?? EMPTY_TOTALS;

  const hasMore = [
    totals.customers,
    totals.sellers,
    totals.transactions,
    totals.teams,
  ].some((total) => nextSkip < total);

  return hasMore ? nextSkip : undefined;
};

const CPF_DIGITS = 11;
const HAS_LETTERS = /[a-zà-ÿ]/i;

const isCpf = (term: string) => term.replace(/\D/g, "").length === CPF_DIGITS;

const canSearchPeople = (term: string) => HAS_LETTERS.test(term) || isCpf(term);

const canSearchCategory = (category: PeopleCategory, term: string) => {
  if (category === SupportCategoryEnum.TEAMS) return isCpf(term);
  return canSearchPeople(term);
};

type SupportPerson = CreateSupportResponse["customers"][number];
type SupportTransaction = CreateSupportResponse["transactions"][number];

export interface SupportSearchResults {
  customers: SupportPerson[];
  sellers: SupportPerson[];
  transactions: SupportTransaction[];
  teams: SupportTeam[];
  supportRequests: SupportRequest[];
}

export type SearchCategoryState<T = boolean> = Record<SearchCategory, T>;

export const useSupportController = () => {
  const [selectedSupport, setSelectedSupport] = useState<SupportRequest | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [comment, setComment] = useState("");
  const [supportTab, setSupportTab] = useState<SupportListTab>("mine");
  const [selectedId, setSelectedId] = useAtom(
    selectedIdfromTableRowTransaction,
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const { isSupport, isAdmin, isSuperAdmin, user } = useAuth();

  const showSupportTools = isSupport || isAdmin;
  const showSupportRequests = useShowSupportRequests();

  const { data, isLoading } = useGetAllSupports({ enabled: showSupportTools });
  const [searchParams, setSearchParams] = useSearchParams();

  const queryClient = useQueryClient();

  useEffect(() => () => setSelectedId(""), [setSelectedId]);

  const linkedTransactionId = searchParams.get(SUPPORT_TRANSACTION_PARAM);
  const linkedSupportNumber = searchParams.get(SUPPORT_REQUEST_PARAM);

  useEffect(() => {
    if (!linkedTransactionId) return;
    setSelectedId(linkedTransactionId);
    setSheetOpen(true);
  }, [linkedTransactionId, setSelectedId]);

  const { data: linkedSupport } = useQuery({
    queryKey: ["supportRequestDetail", linkedSupportNumber],
    queryFn: () =>
      supportRequestService.getDetailByNumber(linkedSupportNumber!),
    enabled: !!linkedSupportNumber,
  });

  // o chamado vem pela URL; depois de aberto, sai dela pra não reabrir
  useEffect(() => {
    if (!linkedSupport) return;
    setSelectedSupport(linkedSupport);
    setSearchParams(
      (params) => {
        params.delete(SUPPORT_REQUEST_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [linkedSupport, setSearchParams]);

  const debouncedSearchValue = useDebounce(1000, searchQuery);
  const searchTerm = (debouncedSearchValue ?? "").trim();

  const { data: productsCatalog } = useQuery<ProductCatalogItem[]>({
    queryKey: ["productsCatalog"],
    queryFn: configService.getProductsCatalog,
  });

  const { data: supportDetail } = useQuery({
    queryKey: ["supportRequestDetail", selectedSupport?.number],
    queryFn: () =>
      supportRequestService.getDetailByNumber(selectedSupport!.number!),
    enabled: !!selectedSupport?.number,
  });

  const { data: documentsData, isLoading: isLoadingDocuments } = useQuery({
    queryKey: ["supportDocuments"],
    queryFn: () =>
      supportService.createSupport({
        search: "",
        categories: [SupportCategoryEnum.TRANSACTION],
        documents: true,
      }),
    enabled: showSupportTools,
    staleTime: 60 * 1000,
  });

  const peopleCategories = useMemo(
    () =>
      PEOPLE_CATEGORIES.filter((category) =>
        canSearchCategory(category, searchTerm),
      ),
    [searchTerm],
  );

  const searchId = useMemo(
    () => (searchTerm ? `${user?._id ?? ""}-${Date.now()}` : ""),
    [searchTerm, user?._id],
  );

  const peopleQuery = useInfiniteQuery({
    queryKey: ["supportSearch", "people", peopleCategories, searchTerm],
    queryFn: ({ pageParam }) =>
      supportService.createSupport({
        search: searchTerm,
        categories: peopleCategories,
        searchId,
        skip: pageParam,
        limit: SEARCH_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: nextSkipOf,
    enabled: !!searchTerm && peopleCategories.length > 0,
    staleTime: 60 * 1000,
  });

  const transactionsQuery = useInfiniteQuery({
    queryKey: ["supportSearch", SupportCategoryEnum.TRANSACTION, searchTerm],
    queryFn: ({ pageParam }) =>
      supportService.createSupport({
        search: searchTerm,
        categories: [SupportCategoryEnum.TRANSACTION],
        searchId,
        skip: pageParam,
        limit: SEARCH_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: nextSkipOf,
    enabled: !!searchTerm,
    staleTime: 60 * 1000,
  });

  // pelo CPF de quem abriu, pelo número ou pelo texto do chamado
  const supportRequestsQuery = useInfiniteQuery({
    queryKey: ["supportSearch", SUPPORT_REQUESTS_CATEGORY, searchTerm],
    queryFn: ({ pageParam }) =>
      supportRequestService.listSupportRequests({
        search: searchTerm,
        searchId,
        sort: "recent",
        skip: pageParam,
        limit: SUPPORT_REQUESTS_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: ListMySupportRequestsResponse) => {
      const nextSkip = lastPage.pagination.skip + lastPage.pagination.limit;
      return nextSkip >= lastPage.pagination.total ? undefined : nextSkip;
    },
    enabled: !!searchTerm,
    staleTime: 60 * 1000,
  });

  const hasCategory = (category: PeopleCategory) =>
    peopleCategories.includes(category);

  const results = useMemo<SupportSearchResults>(() => {
    const peoplePages = peopleQuery.data?.pages ?? [];
    const transactionPages = transactionsQuery.data?.pages ?? [];

    return {
      customers: peoplePages.flatMap((page) => page.customers ?? []),
      sellers: peoplePages.flatMap((page) => page.sellers ?? []),
      teams: peoplePages.flatMap((page) => page.teams ?? []),
      transactions: transactionPages.flatMap((page) => page.transactions ?? []),
      supportRequests:
        supportRequestsQuery.data?.pages.flatMap(
          (page) => page.supportRequests,
        ) ?? [],
    };
  }, [peopleQuery.data, transactionsQuery.data, supportRequestsQuery.data]);

  const searchTotals = useMemo<SearchCategoryState<number>>(() => {
    const people = totalsOf(peopleQuery.data);
    const { transactions } = totalsOf(transactionsQuery.data);

    return {
      [SupportCategoryEnum.CUSTOMER]: people.customers,
      [SupportCategoryEnum.USER]: people.sellers,
      [SupportCategoryEnum.TEAMS]: people.teams,
      [SupportCategoryEnum.TRANSACTION]: transactions,
      [SUPPORT_REQUESTS_CATEGORY]:
        supportRequestsQuery.data?.pages[0]?.pagination.total ?? 0,
    };
  }, [peopleQuery.data, transactionsQuery.data, supportRequestsQuery.data]);

  const searchPages = useMemo(
    () => ({
      people: {
        hasNextPage: peopleQuery.hasNextPage,
        isFetchingNextPage: peopleQuery.isFetchingNextPage,
        fetchNextPage: () => peopleQuery.fetchNextPage(),
      },
      transactions: {
        hasNextPage: transactionsQuery.hasNextPage,
        isFetchingNextPage: transactionsQuery.isFetchingNextPage,
        fetchNextPage: () => transactionsQuery.fetchNextPage(),
      },
      supportRequests: {
        hasNextPage: supportRequestsQuery.hasNextPage,
        isFetchingNextPage: supportRequestsQuery.isFetchingNextPage,
        fetchNextPage: () => supportRequestsQuery.fetchNextPage(),
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      peopleQuery.hasNextPage,
      peopleQuery.isFetchingNextPage,
      transactionsQuery.hasNextPage,
      transactionsQuery.isFetchingNextPage,
      supportRequestsQuery.hasNextPage,
      supportRequestsQuery.isFetchingNextPage,
    ],
  );

  // as abas de pessoa compartilham o estado da chamada única
  const pending = useMemo<SearchCategoryState>(
    () => ({
      [SupportCategoryEnum.CUSTOMER]:
        peopleQuery.isFetching && hasCategory(SupportCategoryEnum.CUSTOMER),
      [SupportCategoryEnum.USER]:
        peopleQuery.isFetching && hasCategory(SupportCategoryEnum.USER),
      [SupportCategoryEnum.TRANSACTION]: transactionsQuery.isFetching,
      [SupportCategoryEnum.TEAMS]:
        peopleQuery.isFetching && hasCategory(SupportCategoryEnum.TEAMS),
      [SUPPORT_REQUESTS_CATEGORY]: supportRequestsQuery.isFetching,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      peopleQuery.isFetching,
      transactionsQuery.isFetching,
      supportRequestsQuery.isFetching,
      peopleCategories,
    ],
  );

  const errors = useMemo<SearchCategoryState>(
    () => ({
      [SupportCategoryEnum.CUSTOMER]:
        peopleQuery.isError && hasCategory(SupportCategoryEnum.CUSTOMER),
      [SupportCategoryEnum.USER]:
        peopleQuery.isError && hasCategory(SupportCategoryEnum.USER),
      [SupportCategoryEnum.TRANSACTION]: transactionsQuery.isError,
      [SupportCategoryEnum.TEAMS]:
        peopleQuery.isError && hasCategory(SupportCategoryEnum.TEAMS),
      [SUPPORT_REQUESTS_CATEGORY]: supportRequestsQuery.isError,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      peopleQuery.isError,
      transactionsQuery.isError,
      supportRequestsQuery.isError,
      peopleCategories,
    ],
  );

  const { mutate: updateSupport, isPending: isUpdatingSupport } = useMutation({
    mutationFn: (payload: {
      status?: SupportRequestStatusEnum;
      comment?: string;
      resolution?: string;
    }) =>
      supportRequestService.updateSupportRequest({
        number: selectedSupport!.number!,
        ...payload,
      }),
    onSuccess: (_data, variables) => {
      toast({
        title: variables.status ? "Chamado atualizado" : "Comentário salvo",
      });
      setComment("");
      queryClient.invalidateQueries({
        queryKey: ["supportRequestDetail", selectedSupport?.number],
      });
      queryClient.invalidateQueries({ queryKey: ["mySupportRequests"] });
      queryClient.invalidateQueries({
        queryKey: [SUPPORT_REQUEST_COUNTS_QUERY_KEY],
      });

      // mesma ideia do "assumir próximo": atualiza o card no lugar em vez de
      // invalidar, senão o chamado que está aberto na tela sai da lista na hora
      const nextStatus = variables.status;
      if (nextStatus) {
        queryClient.setQueriesData<InfiniteData<ListMySupportRequestsResponse>>(
          { queryKey: [SUPPORT_LIST_QUERY_KEY] },
          (current) =>
            current && {
              ...current,
              pages: current.pages.map((page) => ({
                ...page,
                supportRequests: page.supportRequests.map((request) =>
                  request._id === selectedSupport?._id
                    ? { ...request, status: nextStatus }
                    : request,
                ),
              })),
            },
        );
      }
    },
    onError: (error) => {
      const message = (error as AxiosError<{ message?: string }>)?.response
        ?.data?.message;
      toast({
        variant: "destructive",
        title: message || "Não foi possível atualizar o chamado",
      });
    },
  });

  const isSupportStaff = isSupport || isAdmin || isSuperAdmin;

  const { data: supportTeam = [] } = useQuery({
    queryKey: ["supportTeam"],
    queryFn: supportRequestService.getSupportTeam,
    enabled: isSupportStaff && !!selectedSupport,
    staleTime: 5 * 60_000,
  });

  const { mutate: assignSupport, isPending: isAssigningSupport } = useMutation({
    mutationFn: (userId?: string) =>
      supportRequestService.assignSupportRequest(
        selectedSupport!.number!,
        userId,
      ),
    onSuccess: (updated) => {
      toast({ title: "Responsável atualizado" });
      queryClient.invalidateQueries({
        queryKey: ["supportRequestDetail", selectedSupport?.number],
      });
      queryClient.invalidateQueries({ queryKey: ["mySupportRequests"] });
      queryClient.invalidateQueries({
        queryKey: [SUPPORT_REQUEST_COUNTS_QUERY_KEY],
      });
      queryClient.setQueriesData<InfiniteData<ListMySupportRequestsResponse>>(
        { queryKey: [SUPPORT_LIST_QUERY_KEY] },
        (current) =>
          current && {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              supportRequests: page.supportRequests.map((request) =>
                request._id === selectedSupport?._id
                  ? {
                      ...request,
                      status: updated.status,
                      assignedTo: updated.assignedTo,
                    }
                  : request,
              ),
            })),
          },
      );
    },
    onError: (error) => {
      const message = (error as AxiosError<{ message?: string }>)?.response
        ?.data?.message;
      toast({
        variant: "destructive",
        title: message || "Não foi possível atualizar o responsável",
      });
    },
  });

  const handleSaveComment = () => {
    if (!comment.trim()) {
      toast({
        variant: "destructive",
        title: "Digite um comentário para salvar",
      });
      return;
    }
    updateSupport({ comment: comment.trim() });
  };

  const handleUpdateStatus = (to: SupportRequestStatusEnum) => {
    const text = comment.trim();
    if (to === SupportRequestStatusEnum.RESOLVED && !text) {
      toast({
        variant: "destructive",
        title: "Informe a resolução no comentário para resolver o chamado",
      });
      return;
    }
    updateSupport({
      status: to,
      comment: text || undefined,
      resolution: to === SupportRequestStatusEnum.RESOLVED ? text : undefined,
    });
  };

  // atualiza o histórico de pesquisa uma vez, quando as chamadas terminam
  const isSearchSettled =
    !!searchTerm &&
    !peopleQuery.isFetching &&
    !transactionsQuery.isFetching &&
    !supportRequestsQuery.isFetching;

  useEffect(() => {
    if (isSearchSettled) {
      queryClient.invalidateQueries({ queryKey: ["getAllSupports"] });
    }
  }, [isSearchSettled, searchTerm, queryClient]);

  // os cards da busca abrem em outra aba pra não perder a pesquisa
  const handleOpenCustomer = (id: string) => openInNewTab(`/clientes/${id}`);

  const handleOpenSeller = (id: string) => openInNewTab(`/vendedores/${id}`);

  const handleOpenTeam = (id: string) => openInNewTab(`/times/detalhes/${id}`);

  const handleOpenTransactionInNewTab = (id: string) =>
    openInNewTab(`/support?${SUPPORT_TRANSACTION_PARAM}=${id}`);

  const handleOpenSupportRequestInNewTab = (request: SupportRequest) =>
    openInNewTab(`/support?${SUPPORT_REQUEST_PARAM}=${request.number}`);

  const handleDocumentRowClick = (item: DocumentTransaction) => {
    setSelectedId(item._id);
    setSheetOpen(true);
  };

  const handleSelectSupport = (support: SupportRequest | null) => {
    setSelectedSupport(support);
    setComment("");
    if (support) handleSearch("");
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setSelectedId("");
    if (query) setSelectedSupport(null);
  };

  return {
    selectedId,
    data,
    isLoading,
    handleOpenCustomer,
    handleOpenSeller,
    handleOpenTeam,
    handleOpenTransactionInNewTab,
    handleOpenSupportRequestInNewTab,
    handleDocumentRowClick,
    handleSearch,
    searchQuery,
    searchTerm,
    results,
    searchTotals,
    searchPages,
    pending,
    errors,
    documentsTransactions: documentsData?.documentsTransactions || [],
    isLoadingDocuments,
    sheetOpen,
    setSheetOpen,
    productsCatalog,
    selectedSupport,
    supportDetail,
    comment,
    showSupportTools,
    showSupportRequests,
    supportTab,
    setSupportTab,
    setComment,
    handleSelectSupport,
    isUpdatingSupport,
    handleSaveComment,
    handleUpdateStatus,
    supportTeam,
    assignSupport,
    isAssigningSupport,
  };
};
